import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, QrCode, Code, BarChart3, Headphones, Shield } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  MessageSquare,
  QrCode,
  Code,
  BarChart3,
  Headphones,
  Shield,
};

const Services = () => {
  const { language, t } = useLanguage();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error('Error fetching services:', error);
        // Fallback data
        setServices([
          { id: '1', title_ar: 'إرسال رسائل جماعية', title_en: 'Bulk Messaging', description_ar: 'أرسل آلاف الرسائل لعملائك بضغطة زر واحدة', description_en: 'Send thousands of messages to your customers with one click', icon: 'MessageSquare' },
          { id: '2', title_ar: 'ربط QR Code', title_en: 'QR Code Integration', description_ar: 'اربط حسابك بالواتساب عبر مسح QR Code بسهولة', description_en: 'Connect your account to WhatsApp by scanning QR Code easily', icon: 'QrCode' },
          { id: '3', title_ar: 'API متكامل', title_en: 'Complete API', description_ar: 'API سهل الاستخدام للتكامل مع أنظمتك الحالية', description_en: 'Easy-to-use API for integration with your existing systems', icon: 'Code' },
          { id: '4', title_ar: 'تقارير وإحصائيات', title_en: 'Reports & Analytics', description_ar: 'تابع حملاتك وتعرف على معدلات الوصول والتفاعل', description_en: 'Track your campaigns and learn about reach and engagement rates', icon: 'BarChart3' },
          { id: '5', title_ar: 'دعم فني 24/7', title_en: '24/7 Technical Support', description_ar: 'فريق دعم متخصص جاهز لمساعدتك في أي وقت', description_en: 'Specialized support team ready to help you anytime', icon: 'Headphones' },
          { id: '6', title_ar: 'حماية وأمان', title_en: 'Security & Protection', description_ar: 'نظام محمي بأعلى معايير الأمان لحماية بياناتك', description_en: 'System protected with highest security standards', icon: 'Shield' },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 },
    },
  };

  return (
    <section id="services" className="section-spacing bg-slate-50/50" data-testid="services-section">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 text-sm font-medium mb-4">
            {t('services_badge')}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4" data-testid="services-title">
            {t('services_title')}{' '}
            <span className="gradient-text">{t('services_title_highlight')}</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('services_subtitle')}
          </p>
        </motion.div>

        {/* Services Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="card-feature animate-pulse">
                <div className="w-14 h-14 rounded-2xl bg-slate-200 mb-4" />
                <div className="h-6 bg-slate-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-slate-200 rounded w-full" />
              </div>
            ))}
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || MessageSquare;
              return (
                <motion.div
                  key={service.id}
                  variants={itemVariants}
                  className="card-feature group cursor-pointer"
                  data-testid={`service-card-${index}`}
                >
                  <div className="w-14 h-14 rounded-2xl bg-emerald-100 flex items-center justify-center mb-4 group-hover:bg-emerald-500 transition-colors duration-300">
                    <IconComponent className="w-7 h-7 text-emerald-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {language === 'ar' ? service.title_ar : service.title_en}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {language === 'ar' ? service.description_ar : service.description_en}
                  </p>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default Services;
